/**
 * Alias values per member, for the line set's "Report display name" column.
 *
 * A line's display name can be typed by hand, but the useful default is one of the dimension's TM1
 * aliases for that element — `Name`, `ID - Name`, `APQ Name`, … — so the column offers them instead of
 * making someone copy a label out of the member picker.
 *
 * One fetch per alias attribute, and the server caches each map with no TTL, so this is cheap after the
 * first line-set view. Failures are SWALLOWED per alias: an unavailable map must leave the column
 * hand-editable rather than blocking the editor.
 *
 * Only twelve dimensions export aliases at all, so an empty result is the normal case for the rest —
 * `fpa_deck_line_item`, the P&L line-item dimension, is one of them.
 */

import { useEffect, useRef, useState } from 'react';
import { getAliasLabels, listHierarchyAliases } from 'src/components/star-grid/services/starGridService';

/** `lower(member)` → the distinct alias values offered for it, in alias-name order. */
export type AliasOptionsByMember = Readonly<Record<string, readonly string[]>>;

export interface UseLineSetAliasOptionsResult {
  readonly optionsByMember: AliasOptionsByMember;
  readonly loading: boolean;
}

export const useLineSetAliasOptions = (dimension: string | undefined, hierarchy: string | undefined): UseLineSetAliasOptionsResult => {
  const [optionsByMember, setOptionsByMember] = useState<AliasOptionsByMember>({});
  const [loading, setLoading] = useState(false);
  const abortRef = useRef<AbortController | undefined>(undefined);

  useEffect(() => {
    abortRef.current?.abort();
    if (!dimension || !hierarchy) {
      setOptionsByMember({});
      // Aborting the in-flight run above cancels its `.finally`, so clear loading here or it sticks.
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);

    listHierarchyAliases(dimension, hierarchy, controller.signal)
      .then(async (response) => {
        const names = response.aliases ?? [];
        if (names.length === 0) return {} as Record<string, string[]>;
        const maps = await Promise.all(
          names.map((name) =>
            getAliasLabels(dimension, hierarchy, name, controller.signal)
              .then((labels) => labels.labels)
              // One unavailable alias must not cost the others their values.
              .catch(() => ({} as Record<string, string>))
          )
        );
        const merged: Record<string, string[]> = {};
        for (const map of maps)
          for (const [member, value] of Object.entries(map)) {
            if (!value) continue;
            // Key by `lower(member)` to honour the `AliasOptionsByMember` contract: consumers look
            // options up by a lowercased member id, so a mixed-case key from a label map would be missed.
            const key = member.toLowerCase();
            const existing = merged[key] ?? [];
            // De-duplicated: several aliases commonly agree on a member's label, and offering the same
            // string twice reads as a broken list.
            if (!existing.includes(value)) merged[key] = [...existing, value];
          }
        return merged;
      })
      .then((merged) => {
        if (!controller.signal.aborted) setOptionsByMember(merged);
      })
      .catch(() => {
        if (!controller.signal.aborted) setOptionsByMember({});
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [dimension, hierarchy]);

  return { optionsByMember, loading };
};
